import { useState, useEffect, useCallback } from 'react';
import { notificationAPI } from '../services/notificationApi';

export const useNotifications = () => {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [countsByCategory, setCountsByCategory] = useState({ by_category: {} });
    const [loading, setLoading] = useState(false);

    // Charger les notifications
    const loadNotifications = useCallback(async (params = {}) => {
        setLoading(true);
        try {
            const data = await notificationAPI.getNotifications(params);
            setNotifications(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Erreur dans useNotifications:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    // Charger le compteur de non lus
    const loadUnreadCount = useCallback(async () => {
        try {
            const data = await notificationAPI.getUnreadCount();
            setUnreadCount(data?.unread_count ?? 0);
        } catch (error) {
            console.error('Erreur lors du chargement du compteur:', error);
        }
    }, []);

    // Charger les compteurs par catégorie (pour badges sur les onglets)
    const loadUnreadCountByCategory = useCallback(async () => {
        try {
            const data = await notificationAPI.getUnreadCountByCategory();
            setCountsByCategory({ by_category: data?.by_category || {} });
        } catch (error) {
            console.error('Erreur lors du chargement des compteurs par catégorie:', error);
        }
    }, []);

    // Marquer comme lu
    const markAsRead = useCallback(async (notificationId) => {
        try {
            await notificationAPI.markAsRead(notificationId);
            // Mettre à jour localement
            setNotifications(prev =>
                prev.map(notif =>
                    notif.id === notificationId
                        ? { ...notif, is_read: true, read_at: new Date().toISOString() }
                        : notif
                )
            );
            setUnreadCount(prev => Math.max(0, prev - 1));
            loadUnreadCountByCategory();
        } catch (error) {
            console.error('Erreur dans markAsRead:', error);
        }
    }, [loadUnreadCountByCategory]);

    // Marquer tout comme lu
    const markAllAsRead = useCallback(async () => {
        try {
            await notificationAPI.markAllAsRead();
            setNotifications(prev =>
                prev.map(notif => ({ ...notif, is_read: true, read_at: new Date().toISOString() }))
            );
            setUnreadCount(0);
        } catch (error) {
            console.error('Erreur dans markAllAsRead:', error);
        }
    }, []);

    // Supprimer une notification
    const deleteNotification = useCallback(async (notificationId) => {
        try {
            await notificationAPI.deleteNotification(notificationId);
            const deletedNotif = notifications.find(n => n.id === notificationId);
            setNotifications(prev => prev.filter(n => n.id !== notificationId));
            if (deletedNotif && !deletedNotif.is_read) {
                setUnreadCount(prev => Math.max(0, prev - 1));
            }
        } catch (error) {
            console.error('Erreur dans deleteNotification:', error);
        }
    }, [notifications]);

    // Créer une notification de test
    const createTestNotification = useCallback(async () => {
        try {
            const result = await notificationAPI.createTestNotification();
            // Recharger les notifications
            await loadNotifications();
            await loadUnreadCount();
            return result;
        } catch (error) {
            console.error('Erreur dans createTestNotification:', error);
        }
    }, [loadNotifications, loadUnreadCount]);

    // Effet pour charger initialement et mettre à jour périodiquement
    useEffect(() => {
        loadNotifications({ limit: 20 });
        loadUnreadCount();
        loadUnreadCountByCategory();

        const interval = setInterval(() => {
            loadNotifications({ limit: 20 });
            loadUnreadCount();
            loadUnreadCountByCategory();
        }, 30000);

        return () => clearInterval(interval);
    }, [loadNotifications, loadUnreadCount, loadUnreadCountByCategory]);

    return {
        notifications,
        unreadCount,
        countsByCategory,
        loading,
        loadNotifications,
        loadUnreadCount,
        loadUnreadCountByCategory,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        createTestNotification,
        refresh: () => {
            loadNotifications({ limit: 20 });
            loadUnreadCount();
            loadUnreadCountByCategory();
        }
    };
};


